import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { dimenticaQuestoTelefono, registraNotifiche } from "@/lib/push";

export interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  company: string;
  city: string;
  province: string;
  brands: string[];
  is_admin: boolean;
  /** La postazione del banco Datacom: vede e risponde a tutte le chat. */
  banco_operatore?: boolean;
  /** Il privilegio della chat col banco: l'admin puo' toglierlo dal pannello. */
  banco_abilitato?: boolean;
  /** Inizio della prova dei 7 giorni. */
  created_at?: string;
  /** Fine dell'abbonamento assegnato da Datacom; null = senza scadenza. */
  scadenza?: string | null;
  /**
   * La partita IVA e' il cancello dal 28/08: senza, l'app non si usa.
   * Chi si iscrive dal modulo la scrive li'; chi entra con Google o Apple non
   * compila niente, quindi arriva qui vuota e la chiede /completa-profilo.
   */
  partita_iva?: string | null;
  telefono?: string | null;
  /** valida | sconosciuta | non_valida. Lo scrive solo la edge function. */
  piva_esito?: string | null;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  emailVerified: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const currentUserIdRef = useRef<string | null>(null);
  const qc = useQueryClient();

  const fetchProfile = useCallback(async (userId: string) => {
    setProfileLoading(true);
    const { data } = await supabase
      .from("profiles")
      // select(*) e non un elenco: nell'intervallo fra deploy e migrazione la
      // colonna `scadenza` non esiste ancora, e un elenco esplicito farebbe
      // fallire la query lasciando l'utente senza profilo (= senza marchi).
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    // Evita race: solo se è ancora l'utente corrente
    if (currentUserIdRef.current === userId) {
      setProfile((data as unknown as Profile | null) ?? null);
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    // 1) Registra il listener PRIMA di getSession (regola Lovable/Supabase)
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      const uid = newSession?.user?.id ?? null;
      // CAMBIO DI PERSONA = MEMORIA PULITA. Le conversazioni, i messaggi, le
      // ultime domande e i calcoli restavano nella cache di React Query anche
      // dopo l'uscita: sul telefono aziendale o sul PC dell'ufficio, chi
      // entrava dopo vedeva per qualche secondo la roba di chi c'era prima
      // (le "ultime domande" in home restano valide 30 secondi). Sono dati di
      // lavoro di un altro installatore: non devono comparire mai.
      if (uid !== currentUserIdRef.current) qc.clear();
      currentUserIdRef.current = uid;
      if (event === "SIGNED_IN") {
        // Migrazione: pulisce vecchie conversazioni salvate in localStorage
        try {
          const toRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k) continue;
            if (
              k.startsWith("datacom.conversations") ||
              k.startsWith("datacom.conversation.") ||
              k.startsWith("datacom.chat.")
            ) {
              toRemove.push(k);
            }
          }
          toRemove.forEach((k) => {
            console.log("[useAuth] migration: removing legacy localStorage key", k);
            localStorage.removeItem(k);
          });
        } catch {
          /* localStorage non disponibile */
        }
      }
      if (!uid) {
        setProfile(null);
        setProfileLoading(false);
      } else {
        // Deferred per evitare deadlock dentro il callback
        setTimeout(() => {
          void fetchProfile(uid);
        }, 0);
      }
    });

    // 2) Sessione iniziale
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      const uid = data.session?.user?.id ?? null;
      currentUserIdRef.current = uid;
      if (uid) void fetchProfile(uid);
      setSessionReady(true);
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, [fetchProfile, qc]);

  /**
   * Il telefono esce dalla rubrica PRIMA di chiudere la sessione.
   *
   * L'ordine non e' un dettaglio: cancellare la riga richiede di essere ancora
   * autenticati (la policy filtra su auth.uid()). Fatto dopo il signOut, il
   * DELETE non tocca niente e non da' errore - il collega che entra dopo
   * continuerebbe a ricevere le notifiche del precedente.
   */
  const signOut = useCallback(async () => {
    await dimenticaQuestoTelefono();
    await supabase.auth.signOut();
  }, []);

  /**
   * Registrazione alle notifiche appena c'e' un utente con l'email confermata.
   *
   * Qui e non all'avvio: la finestra del permesso si puo' mostrare una volta
   * sola, e chiederla a chi non ha ancora capito cos'e' l'app significa
   * prendersi un "no" definitivo. Sul sito la funzione esce da sola.
   */
  useEffect(() => {
    if (!user?.email_confirmed_at) return;
    void registraNotifiche();
  }, [user?.id, user?.email_confirmed_at]);

  /**
   * La partita IVA si controlla da sola, al primo accesso utile.
   *
   * Chi si iscrive scrive il numero nel modulo, ma il verdetto lo da' il
   * server: qui si chiede solo che lo dia. Parte una volta - quando l'esito e'
   * ancora vuoto - e non blocca niente: se il registro europeo non risponde,
   * l'installatore usa l'app lo stesso e il pannello lo mostra fra quelli da
   * guardare. Far dipendere l'ingresso da un servizio che non e' nostro
   * sarebbe il modo piu' rapido per tenere fuori un cliente vero.
   */
  useEffect(() => {
    if (!profile?.partita_iva) return;
    if (profile.piva_esito) return;
    void supabase.functions
      .invoke("verifica-piva")
      .then(() => fetchProfile(profile.id))
      .catch(() => {});
  }, [profile?.id, profile?.partita_iva, profile?.piva_esito, fetchProfile]);

  const refreshProfile = useCallback(async () => {
    if (user?.id) await fetchProfile(user.id);
  }, [user?.id, fetchProfile]);

  const value = useMemo<AuthContextValue>(() => {
    const isAuthenticated = !!user;
    const isLoading =
      !sessionReady || (isAuthenticated && profile === null && profileLoading);
    return {
      user,
      session,
      profile,
      isLoading,
      isAuthenticated,
      emailVerified: !!user?.email_confirmed_at,
      isAdmin: profile?.is_admin === true,
      signOut,
      refreshProfile,
    };
  }, [user, session, profile, sessionReady, profileLoading, signOut, refreshProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Come useAuth, ma senza pretendere il provider: fuori dal provider (o prima del
 * login) restituisce null invece di lanciare. Serve agli strumenti, che devono
 * funzionare anche in uno smoke test renderizzato nudo.
 */
export function useProfiloOpzionale(): Profile | null {
  const ctx = useContext(AuthContext);
  return ctx?.profile ?? null;
}

/** L'utente autenticato, o null: per le pagine raggiungibili anche da fuori.
 * L'email sta QUI e non nel profilo: profiles non ha una colonna email,
 * l'indirizzo vive in auth.users. */
export function useUtenteOpzionale(): User | null {
  const ctx = useContext(AuthContext);
  return ctx?.user ?? null;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}